import {ExternalLayerImported, LayerId} from '@mc/react-mapio';
import {Feature, FeatureCollection} from 'geojson';

/**
 * Pomocné funkce pro práci s daty externích (připojených / exportovaných) vrstev.
 *
 * Externí vrstvy drží svá data (objekty i jejich atributy) klientsky v mapioContextu
 * jako GeoJSON `FeatureCollection`. Na rozdíl od interních vrstev se tedy data NEnačítají
 * ze serveru – atributová tabulka nad nimi proto pracuje čistě klientsky (useFinalClientTable).
 */

/** Řádek atributové tabulky externí vrstvy – `id` (kvůli IData) + ploché atributy z `properties`. */
export type ExternalLayerRow = {
    id: string | number;
    [key: string]: unknown;
};

/** Interní klíče properties, které do atributové tabulky nepatří (slouží jen k párování vrstev). */
const HIDDEN_PROPERTY_KEYS = new Set(['layerId', 'Layer']);

/** Vyhledá importovanou externí vrstvu podle jejího id. */
export const findExternalLayer = (externalLayers: ExternalLayerImported[] | undefined, layerId: LayerId): ExternalLayerImported | undefined =>
    externalLayers?.find(layer => String(layer.id) === String(layerId));

/** Vrátí true, pokud daná vrstva je externí (připojená / exportovaná). */
export const isExternalLayer = (externalLayers: ExternalLayerImported[] | undefined, layerId: LayerId): boolean =>
    Boolean(findExternalLayer(externalLayers, layerId));

/**
 * Normalizuje `data` externí vrstvy (může být pole FeatureCollection nebo jeden objekt)
 * a vrátí plochý seznam všech features.
 */
const collectFeatures = (layer: ExternalLayerImported): Feature[] => {
    const collections: FeatureCollection[] = Array.isArray(layer.data) ? layer.data : [layer.data as unknown as FeatureCollection];
    return collections.flatMap(collection => collection?.features ?? []);
};

/** Sestaví řádky atributové tabulky z features externí vrstvy. */
export const getExternalLayerRows = (layer: ExternalLayerImported | undefined): ExternalLayerRow[] => {
    if (!layer) return [];
    return collectFeatures(layer).map((feature, index) => ({
        id: feature.id ?? index,
        ...(feature.properties ?? {}),
    }));
};

/**
 * Sestaví seznam atributových sloupců (sjednocení klíčů `properties` napříč všemi features),
 * zachová pořadí prvního výskytu a vynechá interní/párovací klíče.
 */
export const getExternalLayerColumnKeys = (layer: ExternalLayerImported | undefined): string[] => {
    if (!layer) return [];
    const keys: string[] = [];
    const seen = new Set<string>();
    collectFeatures(layer).forEach(feature => {
        Object.keys(feature.properties ?? {}).forEach(key => {
            if (!seen.has(key) && !HIDDEN_PROPERTY_KEYS.has(key)) {
                seen.add(key);
                keys.push(key);
            }
        });
    });
    return keys;
};
